import { useTranslations } from 'use-intl';
import { ConsultantTodoCard } from './ConsultantTodoCard';
import { ConsultantTodoGroupCard } from './ConsultantTodoGroupCard';
import type { TodoItem, TodoAudience } from '@/types/todo';

// The todo types with a catalog label (`consultantTodos.types`); any other type shows its own name.
const LABELLED_TYPES = ['upload_document', 'classify_document', 'doc_without_transaction', 'reconnect_tax_account', 'pay_payable'] as const;
type LabelledType = (typeof LABELLED_TYPES)[number];

function isLabelledType(type: string): type is LabelledType {
  return (LABELLED_TYPES as readonly string[]).includes(type);
}

// FIN-578 PR1 — groups by (type, audience): a group only ever contains one audience,
// since "Send N to client" (PR3) only makes sense over a homogeneous-audience group —
// an already-client-audience row never joins an internal group headed for a bulk send.
//
// FIN-876 — company is part of the key too. On the cross-company admin list a group would
// otherwise span tenants, and its "Send N to client" would redirect several unrelated clients'
// work in one click. Including company_id keeps every bulk send inside one company. On the
// single-company list every row shares a company_id, so the key is unchanged in effect.
function groupTodos(todos: TodoItem[]): Array<{ type: string; audience: TodoAudience; companyId: string; items: TodoItem[] }> {
  const buckets = new Map<string, { type: string; audience: TodoAudience; companyId: string; items: TodoItem[] }>();
  for (const todo of todos) {
    const key = `${todo.type}::${todo.audience}::${todo.company_id}`;
    const bucket = buckets.get(key) ?? { type: todo.type, audience: todo.audience, companyId: todo.company_id, items: [] };
    bucket.items.push(todo);
    buckets.set(key, bucket);
  }
  return Array.from(buckets.values());
}

export function ConsultantTodoList({
  todos,
  showCompanyLabel = false,
  emptyMessage,
}: {
  todos: TodoItem[];
  /** Cross-company list only — the single-company page already knows whose todos these are. */
  showCompanyLabel?: boolean;
  emptyMessage?: string;
}) {
  const t = useTranslations('consultantTodos');

  if (todos.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage ?? t('page.emptyCompany')}</p>;
  }

  const byType = new Map<string, TodoItem[]>();
  for (const todo of todos) {
    byType.set(todo.type, [...(byType.get(todo.type) ?? []), todo]);
  }

  return (
    <>
      {Array.from(byType.entries()).map(([type, typeTodos]) => (
        <section key={type} className="flex flex-col gap-3" aria-labelledby={`${type}-todos-heading`}>
          <h2 id={`${type}-todos-heading`} className="text-sm font-bold tracking-tight">
            {isLabelledType(type) ? t(`types.${type}`) : type.replace(/_/g, ' ')}
          </h2>
          <div className="flex flex-col gap-2">
            {groupTodos(typeTodos).map((bucket) =>
              bucket.items.length === 1 ? (
                <ConsultantTodoCard
                  key={bucket.items[0].id}
                  todo={bucket.items[0]}
                  showCompanyLabel={showCompanyLabel}
                />
              ) : (
                <ConsultantTodoGroupCard
                  key={`${bucket.type}::${bucket.audience}::${bucket.companyId}`}
                  todos={bucket.items}
                  showCompanyLabel={showCompanyLabel}
                />
              )
            )}
          </div>
        </section>
      ))}
    </>
  );
}
